import React, { useState, useEffect } from 'react';
import { History, TrendingUp, TrendingDown, ChevronRight, Calendar, MessageCircle } from 'lucide-react';
import { apiClient } from '../../api/client';

const ConversationFlashbackCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    apiClient.request('/api/enhancements/conversation-flashback')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid="flashback-card" className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
        <div className="h-28 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!data?.has_flashback) {
    return (
      <div data-testid="flashback-card" className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
            <History className="w-5 h-5" />
          </div>
          <h3 className="font-bold text-gray-900">Flashbacks</h3>
        </div>
        <p className="text-sm text-gray-500">{data?.message || 'Keep practising! Your first flashback will appear after 30 days.'}</p>
      </div>
    );
  }

  const fb = data.flashback;
  const improved = data.score_improved;
  const change = Math.abs(data.score_change);

  return (
    <div data-testid="flashback-card" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">On This Day</h3>
              <p className="text-xs text-gray-400">{fb.label}</p>
            </div>
          </div>
          <span className="text-xs text-gray-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> {fb.date}</span>
        </div>

        {/* Flashback content */}
        <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-4 mb-4">
          <p className="text-xs text-violet-500 font-medium uppercase tracking-wider mb-1">{fb.label}</p>
          <p className="text-sm text-gray-700 mb-2">You practised <span className="font-semibold">&quot;{fb.session_topic}&quot;</span> with {fb.ava_name}</p>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-gray-500">
              <MessageCircle className="w-3.5 h-3.5" /> {fb.message_count} messages
            </span>
            <span className="text-gray-500">Score: {fb.old_score}/100</span>
          </div>
        </div>

        {/* Growth comparison */}
        <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-400">{fb.old_score}</p>
            <p className="text-xs text-gray-400">Then</p>
          </div>
          <div className="flex-1 flex items-center justify-center">
            {improved ? (
              <div className="flex items-center gap-1 text-emerald-600">
                <TrendingUp className="w-5 h-5" />
                <span className="text-sm font-bold">+{change}</span>
              </div>
            ) : change > 0 ? (
              <div className="flex items-center gap-1 text-red-400">
                <TrendingDown className="w-5 h-5" />
                <span className="text-sm font-bold">-{change}</span>
              </div>
            ) : (
              <span className="text-sm text-gray-400 font-medium">Same</span>
            )}
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold" style={{ color: improved ? '#10b981' : '#6b7280' }}>{data.current_avg_score}</p>
            <p className="text-xs text-gray-400">Now</p>
          </div>
        </div>

        {improved && (
          <p className="text-sm text-emerald-600 font-medium mt-3 text-center">
            You&apos;ve grown {change} points since then! Keep going!
          </p>
        )}

        {/* Show all flashbacks */}
        {data.all_flashbacks?.length > 1 && (
          <>
            <button
              data-testid="toggle-flashbacks"
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-3 flex items-center justify-between text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              <span>More memories ({data.all_flashbacks.length})</span>
              <ChevronRight className={`w-4 h-4 transition-transform ${showAll ? 'rotate-90' : ''}`} />
            </button>

            {showAll && (
              <div className="mt-2 space-y-2">
                {data.all_flashbacks.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50">
                    <span className="text-xs text-gray-400 w-16">{f.label}</span>
                    <p className="text-sm text-gray-700 flex-1 truncate">{f.session_topic}</p>
                    <span className="text-xs font-bold text-gray-500">{f.old_score}/100</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationFlashbackCard;
